import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { useNova } from '../../lib/store';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, tempPassword, Toggle, useLoad, when } from './kit';

interface Org {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string | null;
  quotas: { agents?: number; licenses?: number | null; concurrent_calls?: number };
  features?: Record<string, boolean>;
  created_at: string;
  users: number;
  agents: number;
  licensesInUse: number;
}

/**
 * The provider's console (platform admins only): every organization on this
 * server, with licenses and agents against what it has bought, and the
 * limits to change. A new organization comes with its first admin.
 */
export function Provider() {
  const [orgs, reload] = useLoad<Org[]>('/platform/orgs');
  const [editing, setEditing] = useState<Org | 'new' | null>(null);
  const done = () => {
    setEditing(null);
    reload();
  };

  return (
    <>
      <SectionHeader
        title="Organizations"
        lead="Every company on this server. Licenses limit how many of their people can be signed in at once; the agent limit caps how many agents they can have. Their admins see their own use under Admin > Licenses."
        action={<AddButton onClick={() => setEditing('new')}>New organization</AddButton>}
      />
      <Table head={['Organization', 'Licenses in use', 'Agents', 'Users', 'Created']} empty={orgs?.length === 0 && 'No organizations yet.'}>
        {(orgs ?? []).map((o) => {
          const cap = o.quotas.licenses ?? null;
          const full = cap !== null && o.licensesInUse >= cap;
          return (
            <Row key={o.id} onClick={() => setEditing(o)}>
              <Cell>
                <span className="flex items-center gap-2 font-medium">
                  <span>{o.name}</span>
                  {o.status === 'suspended' ? <Pill tone="bad">Suspended</Pill> : null}
                </span>
                <span className="text-xs text-ink-3">{o.slug}</span>
              </Cell>
              <Cell className="tabular">
                {o.licensesInUse}
                {cap !== null ? <span className="text-ink-3"> of {cap}</span> : <span className="text-ink-3"> (no limit)</span>}
                {full ? <span className="ml-2"><Pill tone="warn">Full</Pill></span> : null}
              </Cell>
              <Cell className="tabular">
                {o.agents}
                <span className="text-ink-3"> of {o.quotas.agents ?? 50}</span>
              </Cell>
              <Cell className="tabular">{o.users}</Cell>
              <Cell className="whitespace-nowrap text-ink-2">{when(o.created_at)}</Cell>
            </Row>
          );
        })}
      </Table>
      {editing === 'new' ? <NewOrg onDone={done} /> : editing ? <Limits org={editing} onDone={done} /> : null}
    </>
  );
}

/** A count field: blank means "no limit" where that is allowed. */
function Count({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => <input id={id} inputMode="numeric" className={`${inputClass} tabular`} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 5))} />}
    </Field>
  );
}

function Limits({ org, onDone }: { org: Org; onDone: () => void }) {
  const [licenses, setLicenses] = useState(org.quotas.licenses ? String(org.quotas.licenses) : '');
  const [agents, setAgents] = useState(String(org.quotas.agents ?? 50));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const ok = await act(
      () => api('PATCH', `/platform/orgs/${org.id}/quotas`, { licenses: licenses ? Number(licenses) : null, agents: Number(agents) || 50 }),
      `${org.name}: limits saved`,
    );
    setBusy(false);
    if (ok) onDone();
  }

  const below = licenses !== '' && Number(licenses) < org.licensesInUse;
  return (
    <Drawer
      title={org.name}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || Number(agents) < 1 || licenses === '0'} onClick={() => void save()}>
            Save limits
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="mb-5 text-sm text-ink-2">
        {org.licensesInUse} signed in now · {org.agents} agents · {org.users} users. Changes apply within seconds and are in the audit log.
      </p>
      <Count label="Licenses" hint="People signed in at once (admins excepted). Leave blank for no limit." value={licenses} onChange={setLicenses} />
      {below ? (
        <p className="-mt-2 mb-4 text-xs text-wrapup">
          {org.licensesInUse} people are signed in now. Nobody is signed out; new sign-ins wait until fewer than {licenses} are in.
        </p>
      ) : null}
      <Count label="Agent limit" hint="Active users with the agent role. Admins, supervisors, QA and managers do not count." value={agents} onChange={setAgents} />
      <AddOns org={org} onDone={onDone} />
      <Account org={org} onDone={onDone} />
    </Drawer>
  );
}

/**
 * Suspend (e.g. for non-payment) or reactivate. Suspended: nobody can sign
 * in, everyone is signed out, callers hear "not in service". Nothing is
 * deleted; reactivating puts it all back.
 */
function Account({ org, onDone }: { org: Org; onDone: () => void }) {
  const mine = useNova((s) => s.user?.org === org.id);
  const [reason, setReason] = useState('');
  const set = async (status: 'active' | 'suspended') => {
    const ok = await act(
      () => api('PATCH', `/platform/orgs/${org.id}/status`, { status, reason: reason.trim() || undefined }).then(() => true),
      status === 'suspended' ? `${org.name} suspended` : `${org.name} reactivated`,
    );
    if (ok) onDone();
  };
  return (
    <section className="mt-6 rounded-lg border border-line p-4" aria-label="Account">
      <p className="text-sm font-medium">Account</p>
      {org.status === 'suspended' ? (
        <>
          <p className="mb-3 mt-1 text-xs text-ink-3">
            Suspended: nobody can sign in and callers hear "not in service". Reactivating lets everyone sign in again and their numbers ring as before.
          </p>
          <Button tone="primary" className="px-4 py-2" onClick={() => void set('active')}>
            Reactivate
          </Button>
        </>
      ) : mine ? (
        <p className="mt-1 text-xs text-ink-3">This is the organization your own account belongs to, so it cannot be suspended from here.</p>
      ) : (
        <>
          <p className="mb-3 mt-1 text-xs text-ink-3">
            Suspending signs everyone out at once and stops sign-ins; new callers hear "not in service". Calls in progress finish. Nothing is deleted.
          </p>
          <Field label="Reason (optional)" hint="Kept in the audit log, e.g. unpaid invoice.">
            {(id) => <input id={id} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <ConfirmButton confirmText={`Suspend ${org.name}`} onConfirm={() => void set('suspended')}>
            Suspend organization
          </ConfirmButton>
        </>
      )}
    </section>
  );
}

function NewOrg({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: '', slug: '', adminName: '', adminEmail: '', licenses: '', agents: '50' });
  const [password] = useState(tempPassword);
  const [created, setCreated] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  async function create() {
    setBusy(true);
    const ok = await act(
      () =>
        api('POST', '/platform/orgs', {
          name: f.name.trim(),
          slug: f.slug,
          admin: { name: f.adminName.trim(), email: f.adminEmail.trim(), password },
          licenses: f.licenses ? Number(f.licenses) : null,
          agents: Number(f.agents) || 50,
        }).then(() => true),
      `${f.name.trim()} created`,
    );
    setBusy(false);
    if (ok) setCreated(true);
  }

  if (created) {
    return (
      <Drawer title="Share the sign-in details" open onClose={onDone} footer={<Button tone="primary" className="px-4 py-2" onClick={onDone}>Done</Button>}>
        <p className="mb-4 text-sm text-ink-2">Send these to {f.adminName.trim()}. The password is shown once; they can change it from their status menu after signing in.</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-xl border border-line p-4 text-sm">
          <dt className="text-ink-3">Email</dt>
          <dd>{f.adminEmail.trim()}</dd>
          <dt className="text-ink-3">Password</dt>
          <dd className="tabular select-all">{password}</dd>
        </dl>
      </Drawer>
    );
  }

  const slugOk = /^[a-z][a-z0-9]{1,19}$/.test(f.slug);
  return (
    <Drawer
      title="New organization"
      open
      onClose={onDone}
      footer={
        <>
          <Button
            tone="primary"
            className="px-4 py-2"
            disabled={busy || f.name.trim().length < 2 || !slugOk || f.adminName.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(f.adminEmail)}
            onClick={() => void create()}
          >
            Create
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Company name">{(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => {
        const name = e.target.value;
        // Suggest a short name from the company name until one is typed
        setF((x) => ({ ...x, name, slug: x.slug && x.slug !== suggest(x.name) ? x.slug : suggest(name) }));
      }} />}</Field>
      <Field label="Short name" hint="2–20 lowercase letters or digits, starting with a letter. Used inside Nova; it cannot change later.">
        {(id) => <input id={id} className={`${inputClass} tabular`} value={f.slug} onChange={(e) => set('slug')(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20))} />}
      </Field>
      <Field label="Their admin's name">{(id) => <input id={id} className={inputClass} value={f.adminName} onChange={(e) => set('adminName')(e.target.value)} />}</Field>
      <Field label="Their admin's email" hint="Their admin sets up users, queues and numbers.">
        {(id) => <input id={id} type="email" className={inputClass} value={f.adminEmail} onChange={(e) => set('adminEmail')(e.target.value)} />}
      </Field>
      <Count label="Licenses" hint="People signed in at once (admins excepted). Leave blank for no limit." value={f.licenses} onChange={set('licenses')} />
      <Count label="Agent limit" hint="Active users with the agent role." value={f.agents} onChange={set('agents')} />
    </Drawer>
  );
}

const suggest = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/^[0-9]+/, '').slice(0, 20);

/** Add-ons for this organization, switched on and off at once (and in the audit log). */
function AddOns({ org, onDone }: { org: Org; onDone: () => void }) {
  const [qa, setQa] = useState(Boolean(org.features?.qa));
  async function flip(v: boolean) {
    const ok = await act(() => api('PATCH', `/platform/orgs/${org.id}/features`, { qa: v }).then(() => true), `${org.name}: Quality ${v ? 'on' : 'off'}`);
    if (ok) {
      setQa(v);
      onDone();
    }
  }
  return (
    <div className="mb-4 rounded-lg border border-line p-3" aria-label="Add-ons">
      <p className="mb-2 text-sm font-medium">Add-ons</p>
      <Toggle label="Quality (QA): scorecards, reviews and quality reports" checked={qa} onChange={(v) => void flip(v)} />
      <p className="-mt-2 text-[12px] text-ink-3">Switching it off hides Quality from the company; its scorecards and reviews are kept for when it is on again.</p>
    </div>
  );
}
